import { Column, columnAlign, type RowData } from '@/lib/tanstack-table';
import { useSelector } from '@tanstack/react-store';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretUpDownIcon,
  EyeSlashIcon,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface DataTableColumnHeaderProps<
  TData extends RowData,
  TValue,
> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  isHideable?: boolean;
}

const SORT_BUTTON_SIZE =
  'h-8 group-data-[density=compact]/table:h-6 group-data-[density=compact]/table:px-2 group-data-[density=compact]/table:text-[11px] group-data-[density=compact]/table:[&_svg]:size-3';

// The ghost button's padding is pulled into the cell's so its title lines up
// with the cell text; on a right-aligned (`meta.align`) column the sort icon
// leads so the title's end lines up with the figures.
const SORT_BUTTON_ALIGN = {
  left: '-ml-3 group-data-[density=compact]/table:-ml-2',
  right:
    'ml-auto -mr-3 flex w-fit flex-row-reverse group-data-[density=compact]/table:-mr-2',
} as const;

export function DataTableColumnHeader<TData extends RowData, TValue>({
  column,
  title,
  className,
  isHideable = false,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const align = columnAlign(column.columnDef.meta);
  const sorting = useSelector(column.table.atoms.sorting);
  const sortIndex = sorting.findIndex((sort) => sort.id === column.id);
  const columnSorting = sorting[sortIndex];
  const sortDirection = columnSorting
    ? columnSorting.desc
      ? 'desc'
      : 'asc'
    : false;
  // Opt-in, so tables that never asked for multi-sort keep single-key clicks.
  const multiSort = column.table.options.enableMultiSort === true;
  const sortLevel =
    multiSort && sorting.length > 1 && sortIndex >= 0 ? sortIndex + 1 : null;

  if (!column.getCanSort()) {
    return (
      <div className={cn(align === 'right' && 'text-right', className)}>
        {title}
      </div>
    );
  }

  if (!isHideable && multiSort) {
    return (
      <MultiSortHeaderButton
        className={cn(SORT_BUTTON_ALIGN[align], SORT_BUTTON_SIZE)}
        onToggle={(addLevel) => column.toggleSorting(undefined, addLevel)}
        sortLevel={sortLevel}
      >
        <span>{title}</span>
        <DataTableSortStatusIcon sortDirection={sortDirection} />
      </MultiSortHeaderButton>
    );
  }

  if (!isHideable) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(SORT_BUTTON_ALIGN[align], SORT_BUTTON_SIZE)}
        onClick={() => column.toggleSorting()}
      >
        <span>{title}</span>
        <DataTableSortStatusIcon sortDirection={sortDirection} />
      </Button>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'data-[popup-open]:bg-accent',
              SORT_BUTTON_ALIGN[align],
              SORT_BUTTON_SIZE,
            )}
          >
            <span>{title}</span>
            <DataTableSortStatusIcon sortDirection={sortDirection} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUpIcon />
            <T context="Table column sort order menu">Asc</T>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDownIcon />
            <T context="Table column sort order menu">Desc</T>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeSlashIcon />
            <T context="Hide table column from view">Hide</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * A sort header on a multi-sort table: shift-click adds the column as the
 * next sort level, and a badge shows which level it is (1 sorts first).
 */
function MultiSortHeaderButton({
  className,
  onToggle,
  sortLevel,
  children,
}: {
  className: string;
  onToggle: (addLevel: boolean) => void;
  sortLevel: number | null;
  children: React.ReactNode;
}) {
  const gt = useGT();
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className}
      onClick={(event) => onToggle(event.shiftKey)}
      title={gt('Shift-click to add a sort level', {
        context:
          'tooltip on a sortable table column header that supports multi-column sort',
      })}
    >
      {children}
      {sortLevel !== null && (
        <span
          className="text-[10px] font-semibold tabular-nums leading-none text-primary"
          aria-label={gt('sort level {level}', {
            context: 'table column header: position in a multi-column sort',
            level: sortLevel,
          })}
        >
          {sortLevel}
        </span>
      )}
    </Button>
  );
}

function DataTableSortStatusIcon({
  sortDirection,
}: {
  sortDirection: false | 'asc' | 'desc';
}) {
  return (
    <>
      {sortDirection === 'desc' ? (
        <ArrowDownIcon />
      ) : sortDirection === 'asc' ? (
        <ArrowUpIcon />
      ) : (
        <CaretUpDownIcon />
      )}
    </>
  );
}
